import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { listApiKeys, createApiKey, revokeApiKey } from '../api/apiKeys';
import { listSalesmen } from '../api/salesmen';
import { useAuth } from '../context/AuthContext';

export default function ApiKeys() {
  const { isAdmin } = useAuth();
  const [keys, setKeys] = useState([]);
  const [salesmen, setSalesmen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salesmanCode, setSalesmanCode] = useState('');
  const [label, setLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [justCreated, setJustCreated] = useState(null);

  async function refresh() {
    setLoading(true);
    setKeys(await listApiKeys());
    setLoading(false);
  }

  useEffect(() => {
    refresh();
    listSalesmen().then(setSalesmen);
  }, []);

  async function handleCreate(e) {
    e.preventDefault();
    if (!salesmanCode) return;
    setCreating(true);
    setError('');
    setJustCreated(null);
    try {
      const result = await createApiKey({ salesmanCode, label });
      setJustCreated(result);
      setLabel('');
      setSalesmanCode('');
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not create key');
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(k) {
    if (!window.confirm(`Revoke this API key for ${k.salesmanName || k.salesmanCode}? It will stop working immediately.`)) return;
    await revokeApiKey(k._id);
    refresh();
  }

  if (!isAdmin) return <Navigate to="/" replace />;

  return (
    <div>
      <h1 className="text-xl font-semibold text-gray-800 mb-4">API Keys</h1>
      <p className="text-sm text-gray-600 mb-4">
        Long-lived keys for external integrations (e.g. the CRM's Price Checker lookup) — unlike a
        user login, these never expire on their own. Each key is bound to exactly one Salesman: a
        request made with it can only ever see that salesman's own customers and sale history,
        regardless of what the caller asks for. No cost, GP, or profit data is ever exposed through
        these keys.
      </p>

      {justCreated && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-4">
          <div className="text-sm font-semibold text-amber-800 mb-1">
            Key created for {justCreated.salesmanName || justCreated.salesmanCode} — copy it now, it won't be shown again
          </div>
          <code className="block bg-white border border-amber-200 rounded px-3 py-2 text-sm font-mono break-all">
            {justCreated.rawKey}
          </code>
          <button
            onClick={() => setJustCreated(null)}
            className="mt-2 text-xs text-amber-700 hover:underline"
          >
            Dismiss
          </button>
        </div>
      )}

      <form onSubmit={handleCreate} className="bg-white border border-gray-200 rounded-lg p-4 mb-6 flex flex-wrap gap-4 items-end">
        {error && (
          <div className="w-full text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>
        )}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Salesman</label>
          <select
            required
            value={salesmanCode}
            onChange={(e) => setSalesmanCode(e.target.value)}
            className="border border-gray-300 rounded px-3 py-1.5 text-sm min-w-[200px]"
          >
            <option value="">Select a salesman…</option>
            {salesmen.map((s) => (
              <option key={s.salesmanCode} value={s.salesmanCode}>
                {s.salesmanName} ({s.salesmanCode})
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Label (optional)</label>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. CRM integration"
            className="border border-gray-300 rounded px-3 py-1.5 text-sm"
          />
        </div>
        <button
          type="submit"
          disabled={creating || !salesmanCode}
          className="px-3 py-2 text-sm rounded bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white"
        >
          {creating ? 'Generating…' : 'Generate Key'}
        </button>
      </form>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-left">
            <tr>
              <th className="px-4 py-2 font-medium">Salesman</th>
              <th className="px-4 py-2 font-medium">Label</th>
              <th className="px-4 py-2 font-medium">Key Prefix</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2 font-medium">Last Used</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-gray-400">
                  Loading…
                </td>
              </tr>
            ) : keys.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-gray-400">
                  No API keys yet
                </td>
              </tr>
            ) : (
              keys.map((k) => (
                <tr key={k._id} className="border-t border-gray-100">
                  <td className="px-4 py-2 text-gray-700">
                    {k.salesmanName} <span className="text-gray-400">({k.salesmanCode})</span>
                  </td>
                  <td className="px-4 py-2 text-gray-500">{k.label || '—'}</td>
                  <td className="px-4 py-2 font-mono text-xs text-gray-500">{k.keyPrefix}…</td>
                  <td className="px-4 py-2">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full border ${
                        k.active
                          ? 'bg-green-50 text-green-700 border-green-200'
                          : 'bg-gray-50 text-gray-400 border-gray-200'
                      }`}
                    >
                      {k.active ? 'Active' : 'Revoked'}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-gray-500 whitespace-nowrap">
                    {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : 'Never'}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {k.active && (
                      <button onClick={() => handleRevoke(k)} className="text-red-500 hover:text-red-700 text-xs">
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
